import { differentiate } from './derivative';
import { evaluateAt } from './evaluate';
import { type Node } from './parse';
import { rootsAmong, snapNumber, typicalMagnitude } from './roots';

/**
 * The points on a curve that a problem is actually about.
 *
 * ## Why a figure needs them
 *
 * "Cho hàm số y = f(x) có đồ thị như hình vẽ. Tìm giá trị cực đại của hàm số." The question is
 * unanswerable from a bare curve: the reader can see *that* there is a maximum and cannot read
 * *what* it is. A textbook figure therefore carries a dot at the turning point with `(-1; 2)`
 * written beside it, and the whole exercise hangs on that one annotation.
 *
 * Before this, the only way to get it was `plot -1 2` — the teacher computing the extremum by
 * hand and typing where it goes. That is fine until the formula changes, and it is worse than
 * fine for the "đề bài → hình" path, where the thing typing the script is a language model:
 * asking a model to do the calculus and be right is exactly the wrong division of labour. It
 * knows what the problem is asking for; the derivative is what a computer is for.
 *
 * ## Why the marks are opt-in
 *
 * Every notable point drawn is one the reader has to look at. `y = sin x` over `[−6.5, 6.5]`
 * has four turning points and five roots, and a figure carrying ten coordinate labels has told
 * the reader nothing about which one the question means. A textbook marks what the exercise
 * needs and leaves the rest of the curve plain, so `marks` is a statement rather than something
 * every graph does — and it takes a filter so "chỉ rõ các điểm cực trị" can be answered with
 * the extrema alone.
 *
 * ## Why nothing here is trusted from where it was found
 *
 * A sign change is not a root. `y = 1/x` changes sign across the origin and has no zero there;
 * bisecting that bracket converges onto the pole and reports a root at `x = 0`, which would put
 * a dot and the label `(0; 0)` on a curve that goes to infinity at that very point. So every
 * candidate is checked at the place it was found — the value must be there, and it must be as
 * small as it claims — and one that fails is dropped rather than drawn.
 */

export const NotableKind = {
  /** A local maximum: `y'` turns from positive to negative. */
  Maximum: 'maximum',
  /** A local minimum. */
  Minimum: 'minimum',
  /** Where the curve crosses `Ox` — a root of the equation, `giao với trục hoành`. */
  Root: 'root',
  /** Where it crosses `Oy`, which is `f(0)` and there is at most one. */
  Intercept: 'intercept',
} as const;
export type NotableKind = (typeof NotableKind)[keyof typeof NotableKind];

export interface NotablePoint {
  readonly kind: NotableKind;
  readonly x: number;
  readonly y: number;
}

/** Samples across the interval. Dense enough that two turning points a tick apart are separate. */
const SCAN_STEPS = 2000;

/** A value this far from zero is not a root, relative to what the function does elsewhere. */
const ZERO_TOLERANCE = 1e-6;

/** Past this the curve is off the page, and a label there marks nothing the reader can see. */
const HUGE = 1e6;

/**
 * The notable points of `y = f(x)` over `[from, to]`, in increasing order of `x`.
 *
 * `kinds` selects what to look for; omitting it looks for everything.
 */
export function notablePointsOf(
  f: Node,
  from: number,
  to: number,
  kinds: readonly NotableKind[] = Object.values(NotableKind)
): readonly NotablePoint[] {
  if (!(to > from)) return [];

  const wanted = new Set(kinds);
  const step = (to - from) / SCAN_STEPS;
  const grid = Array.from({ length: SCAN_STEPS + 1 }, (_, index) => from + index * step);

  const scale = typicalMagnitude(grid.map((x) => evaluateAt(f, x)));
  const found: NotablePoint[] = [];

  /** The point `(x, f(x))`, or `null` if the curve is not there to be marked. */
  const on = (x: number, kind: NotableKind): NotablePoint | null => {
    const y = evaluateAt(f, x);
    if (y === null || !Number.isFinite(y) || Math.abs(y) > HUGE) return null;
    return { kind, x: snapNumber(x), y: snapNumber(y) };
  };

  if (wanted.has(NotableKind.Maximum) || wanted.has(NotableKind.Minimum)) {
    const derivative = differentiate(f);

    /*
     * `null` means the function has no derivative — `floor`, `sign`, `min`. There is no
     * numeric fallback worth having here: a difference quotient near a turning point is a
     * small difference of two nearly equal numbers, and its *sign*, which is the only thing
     * being asked, is noise. The honest answer is no marks.
     */
    if (derivative !== null) {
      const gradientScale = typicalMagnitude(grid.map((x) => evaluateAt(derivative, x)));

      for (const x of rootsAmong(derivative, grid)) {
        const gradient = evaluateAt(derivative, x);
        if (gradient === null || Math.abs(gradient) > gradientScale * ZERO_TOLERANCE) continue;

        /*
         * Which side of a turn this is, read from the sign of `y'` on either side.
         *
         * A stationary point is not always an extremum, and drawing it as one asserts
         * something false: `y = x³` is flat at the origin and rises straight through it, and
         * "hàm số không có cực trị" is the answer to the question about it. Rising on both
         * sides — or falling on both — is not a turning point and gets no mark.
         */
        const reach = Math.max(Math.abs(step) * 4, Math.abs(x) * 1e-6);
        const before = evaluateAt(derivative, x - reach);
        const after = evaluateAt(derivative, x + reach);
        if (before === null || after === null) continue;

        const kind =
          before > 0 && after < 0
            ? NotableKind.Maximum
            : before < 0 && after > 0
              ? NotableKind.Minimum
              : null;
        if (kind === null || !wanted.has(kind)) continue;

        const point = on(x, kind);
        if (point !== null) found.push(point);
      }
    }
  }

  if (wanted.has(NotableKind.Root)) {
    for (const x of rootsAmong(f, grid)) {
      const y = evaluateAt(f, x);
      if (y === null || Math.abs(y) > scale * ZERO_TOLERANCE) continue;
      found.push({ kind: NotableKind.Root, x: snapNumber(x), y: 0 });
    }
  }

  if (wanted.has(NotableKind.Intercept) && from <= 0 && to >= 0) {
    const point = on(0, NotableKind.Intercept);
    if (point !== null) found.push(point);
  }

  /*
   * Two marks at the same place is one dot drawn twice and two labels on top of each other.
   * It happens for real: `y = x²` has a minimum, a root and a y-intercept all at the origin,
   * and `y = x³ − 3x` has a root and its y-intercept there. The earlier kind wins, which is
   * the more specific one — a turning point that happens to sit on the axis is still what the
   * exercise is about.
   */
  const kept: NotablePoint[] = [];
  for (const point of [...found].sort((a, b) => a.x - b.x)) {
    const last = kept[kept.length - 1];
    if (last === undefined || Math.abs(point.x - last.x) > Math.abs(step)) kept.push(point);
  }
  return kept;
}
